<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, onMounted, ref } from "vue";
import { App } from "@capacitor/app";
import { useI18n } from "vue-i18n";

import PinCode from "./TcPinCode.vue";
import { TcButton as Button, TcModal } from "@/Components/UI";

import useAppStore from "@/Modules/App/Store";
import useAuthStore from "@/Modules/Auth/Store";
import { NotificationType } from "@/Modules/App/Models/Domain/NotificationType";

import { isApp } from "@/Utils/Ionic";
import { log } from "@/Utils/Debug";
import { isValidString } from "@/Utils/String";

import { config } from "@/Common/envConfig";

import logoSrc from "@/Assets/images/icon-192.png";
import usePrivacyStore from "@/Modules/Privacy/Store";

const appStore = useAppStore();
const authStore = useAuthStore();
const privacyStore = usePrivacyStore();
const { t } = useI18n();

const needProtection = ref<boolean>(isApp());
const needPin = ref<boolean>(false);
const newPin = ref<string>("");
const canLogout = ref<boolean>(false);
const isErrorPin = ref<boolean>(false);
const isCloseSessionModalOpened = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const pinLockRemainingSeconds = ref<number>(privacyStore.getPinLockRemainingSeconds());
const pinInputVersion = ref<number>(0);
let pinValidationVersion = 0;
let pinLockInterval: number | undefined;

const logger = (...args: unknown[]) => {
  log("PRIVACY", ...args);
};

const isValidPin = computed(
  () => isValidString(newPin.value) && newPin.value.trim().length === config.privacyCodeLength
);
const isPinLocked = computed(() => pinLockRemainingSeconds.value > 0);
const formattedPinLockTime = computed(() => {
  const minutes = Math.floor(pinLockRemainingSeconds.value / 60);
  const seconds = pinLockRemainingSeconds.value % 60;

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
});

const stopPinLockCountdown = () => {
  if (pinLockInterval !== undefined) {
    window.clearInterval(pinLockInterval);
    pinLockInterval = undefined;
  }
};

const updatePinLockRemainingSeconds = () => {
  pinLockRemainingSeconds.value = privacyStore.getPinLockRemainingSeconds();

  if (!isPinLocked.value) {
    stopPinLockCountdown();
    isErrorPin.value = false;
  }
};

const startPinLockCountdown = () => {
  updatePinLockRemainingSeconds();

  if (!isPinLocked.value || pinLockInterval !== undefined) {
    return;
  }

  pinLockInterval = window.setInterval(updatePinLockRemainingSeconds, 1000);
};

const requestPrivacyUnlock = async () => {
  if (!privacyStore.needProtection()) {
    needProtection.value = false;
    needPin.value = false;

    return;
  }

  const validationVersion = pinValidationVersion;

  needProtection.value = true;
  startPinLockCountdown();

  if (privacyStore.isBioEnabled()) {
    const isVerified = await privacyStore.checkBiometric(true);

    if (validationVersion !== pinValidationVersion) {
      return;
    }

    logger(`BIO CHECKER -------------- ${isVerified}`);

    if (isVerified) {
      needProtection.value = false;
      needPin.value = false;

      return;
    }

    logger("BIO NO VERIFICADO");
  }

  needPin.value = true;
  canLogout.value = true;
};

const handleChangePin = (val: string) => {
  newPin.value = val;
};

const showPrivacyError = () => {
  canLogout.value = true;

  appStore.showSnackbar({
    message: "errors.general.title",
    type: NotificationType.ERROR,
  });
};

const handleConfirm = async () => {
  if (isLoading.value || isPinLocked.value || !isValidPin.value) {
    return;
  }

  const validationVersion = ++pinValidationVersion;

  isLoading.value = true;
  isErrorPin.value = false;

  try {
    const isValid = await privacyStore.validatePin(newPin.value);

    if (validationVersion !== pinValidationVersion) {
      return;
    }

    if (isValid) {
      logger("pin valido");
      needProtection.value = false;
      newPin.value = "";
    } else {
      logger("pin invalido");
      canLogout.value = true;
      isErrorPin.value = true;
      newPin.value = "";
      pinInputVersion.value++;

      startPinLockCountdown();
    }
  } catch {
    if (validationVersion === pinValidationVersion) {
      showPrivacyError();
    }
  } finally {
    if (validationVersion === pinValidationVersion) {
      isLoading.value = false;
    }
  }
};

const showCloseSessionModal = () => {
  isCloseSessionModalOpened.value = true;
};

const hideCloseSessionModal = () => {
  isCloseSessionModalOpened.value = false;
};

const logout = () => {
  pinValidationVersion++;
  isLoading.value = false;

  authStore.logout();

  needProtection.value = false;
};

onBeforeMount(async () => {
  if (!isApp()) {
    return;
  }

  needPin.value = false;

  try {
    if (!authStore.isUserLogged) {
      logger("NOT AUTH");

      await privacyStore.setIsPrivate(false).catch(showPrivacyError);

      needProtection.value = false;

      return;
    }

    await privacyStore.initialize();

    logger("IS PRIVATE", privacyStore.isPrivate);

    if (privacyStore.isPrivate) {
      await requestPrivacyUnlock();
    } else {
      needProtection.value = false;
    }
  } catch {
    needProtection.value = true;
    needPin.value = true;

    showPrivacyError();
  }
});

onMounted(() => {
  if (isApp()) {
    startPinLockCountdown();

    App.addListener("appStateChange", async ({ isActive }) => {
      pinValidationVersion++;
      isLoading.value = false;

      if (!isActive && privacyStore.isPrivate && !needProtection.value) {
        await privacyStore.updatePinDate().catch(showPrivacyError);
      }

      appStore.updateAppStatus(isActive);
      needPin.value = false;
      isErrorPin.value = false;

      if (!authStore.isUserLogged) {
        logger("NOT AUTH onMounted");

        await privacyStore.setIsPrivate(false).catch(showPrivacyError);

        needProtection.value = false;
        return;
      }

      logger("CHANGE APP STATE");
      if (isActive) {
        try {
          await privacyStore.initialize();
          await requestPrivacyUnlock();
        } catch {
          needProtection.value = true;
          needPin.value = true;

          showPrivacyError();
        }
      }

      logger("App state changed. Is active?", isActive);
    });

    App.addListener("appRestoredResult", (data) => {
      logger("Restored state:", data);
    });
  }
});

onBeforeUnmount(() => {
  pinValidationVersion++;
  stopPinLockCountdown();
});
</script>

<template>
  <slot v-if="!needProtection || !isApp()" />

  <div class="privacy_view" v-else :class="{ 'privacy_view--pin': needPin }">
    <div class="privacy_view__pin" v-if="needPin">
      <p class="text-center typography-low-l color-content-mid mb-8">
        {{ t("privacy.description") }}
      </p>

      <PinCode
        :key="pinInputVersion"
        name="privacy_screen_app"
        :class="{ 'mb-8': !isErrorPin && !isPinLocked, 'mb-2': isErrorPin || isPinLocked }"
        :length="config.privacyCodeLength"
        :hasError="isErrorPin"
        :disabled="isLoading || isPinLocked"
        @change="handleChangePin"
      />

      <p class="color-semantic-error mb-8 typography-low-s text-center" v-if="isPinLocked">
        {{ t("privacy.pin_locked", { time: formattedPinLockTime }) }}
      </p>

      <p class="color-semantic-error mb-8 typography-low-s text-center" v-else-if="isErrorPin">
        {{ t("privacy.pin_error") }}
      </p>

      <Button
        class="privacy_view__pin__confirm"
        :disabled="!isValidPin || isLoading || isPinLocked"
        :isLoading="isLoading"
        @click="handleConfirm"
      >
        {{ t("privacy.confirm") }}
      </Button>

      <TcModal
        v-if="isCloseSessionModalOpened"
        :acceptButtonText="t('settings.modal.logout')"
        :rejectButtonText="t('settings.modal.cancel')"
        :title="t('settings.modal.close_session')"
        :textContent="t('settings.modal.are_you_sure_to_close_session')"
        @acceptButtonClicked="logout"
        @rejectButtonClicked="hideCloseSessionModal"
      />

      <Button
        v-if="canLogout"
        class="privacy_view__pin__logout mt-5"
        variant="no_border"
        @click="showCloseSessionModal"
      >
        <span class="privacy_view__logout__text">{{ t("settings.modal.logout") }}</span>
      </Button>
    </div>

    <img v-else class="privacy_view__logo" :src="logoSrc" alt="therapyside logo" />
  </div>
</template>

<style scoped lang="scss">
.privacy_view {
  @include sp.padding(b, 23);

  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  &--pin {
    @include colors.bg(background, base);

    align-items: start;
  }

  &__logo {
    height: 10vh;
  }

  &__pin {
    @include sp.margin(t, 20);
    @include sp.padding(x, 4);

    max-width: 400px;
    width: 100%;

    &__confirm {
      max-width: 240px;
      margin: auto;
      display: block;
    }

    &__logout {
      @include sp.margin(b, 4);

      flex-grow: 0;

      &__text {
        @include text.general(high, s);
        @include colors.color(content, high);
      }
    }
  }
}
</style>
